/* eslint-disable arrow-body-style */
/* eslint-disable jsx-a11y/no-noninteractive-element-interactions */
import { useOwnerProps } from '@elliemae/ds-props-helpers';
import type { DSSwipeCardT } from '@elliemae/ds-swipe-card/src/react-desc-prop-types.js';
import React, { useCallback, useContext, useMemo } from 'react';
import { DSTabsContext } from '../../DSTabsCTX.js';
import type { DSTabT } from '../../react-desc-prop-types.js';
import type { PanelDescriptor } from './styles.js';
import { StyledDSSwipeCard, StyledPanelContainer } from './styles.js';
/**
 * Describes the data needed to render a single Tab panel slide.
 */

/**
 * Responsible for rendering a single slide using the provided panel descriptor.
 * Receives a `dsIndex` prop from DSSwipeCard which indicates the slide position.
 */
const CardRender: React.ComponentType<DSSwipeCardT.CardRenderProps<PanelDescriptor>> = (props) => {
  const { content, tabPanelsProps, isDSMobile, hide, style, role, tabId, renderKey, ...rest } = props;
  // We pass through all attributes, including accessibility and dataset attributes.
  return (
    <StyledPanelContainer
      id={tabId}
      {...tabPanelsProps}
      {...rest}
      // Styled component specific props
      isDSMobile={isDSMobile}
      hide={hide}
      style={style}
      role={role}
    >
      {content}
    </StyledPanelContainer>
  );
};

export const TabsPanels = (): JSX.Element => {
  const {
    actualActiveTab,
    focusableTabsRef,
    props,
    props: { onlyRenderActiveTab, children: tabs, isDSMobile, tabPanelsProps, animated },
    globalClickHandler,
    userDidChangeTabAtleastOnceRef,
  } = useContext(DSTabsContext);

  const { getOwnerProps: getTabPanelsOwnerProps, getOwnerPropsArguments: getTabPanelsOwnerPropsArguments } =
    useOwnerProps(props);

  /** [PUI-15772] - Tabs Pre Refactor
   * Handler for swipe gestures and tab index changes. It:
   * 1. Calculates the offset between current and new tab index
   * 2. Finds the new focusable tab based on the offset
   * 3. Updates the active tab and forces a click event
   * 4. May cause unnecessary rerenders due to forced click events
   */
  const handleOnChangeIndex = useCallback(
    (index: number, indexLatest: number) => {
      if (!focusableTabsRef.current) return;

      const offset = index - indexLatest;

      if (focusableTabsRef.current) {
        const currentIndexFocusable = focusableTabsRef.current?.findIndex((el) => el.dataset.tabId === actualActiveTab);

        const newFocusableIndex = currentIndexFocusable >= 0 ? currentIndexFocusable + offset : -1;

        if (
          newFocusableIndex < 0 ||
          newFocusableIndex > focusableTabsRef.current?.length ||
          !focusableTabsRef.current?.[newFocusableIndex]
        )
          return;
        globalClickHandler({
          event: { type: 'click' } as React.MouseEvent<HTMLDivElement>,
          target: 'swipeableViews',
          tabId: focusableTabsRef.current[newFocusableIndex].dataset.tabId,
        });

        // swiping does not trigger centerTab function inside handleOnTabChange because there is no event
        // by clicking we are manually forcing to center the tab

        focusableTabsRef.current[newFocusableIndex].click();
      }
    },
    [actualActiveTab, focusableTabsRef, globalClickHandler],
  );

  const panels: PanelDescriptor[] = useMemo(() => {
    // To preserve indices for swipe behavior, we return a fully-typed placeholder panel
    // instead of null when a panel should not be rendered. This keeps array length stable.
    const mapResult = React.Children.map(tabs, (tab: React.ReactElement<DSTabT.Props>, index: number) => {
      const { tabId: panelId = '', style, children: content, disabled, applyAriaDisabled } = tab.props;
      const isActive = actualActiveTab === panelId;
      const shouldRender = !onlyRenderActiveTab || isActive;
      const getOwnerProps = () => tab.props;
      const getOwnerPropsArguments = () => ({ panelId });

      const baseDescriptor: PanelDescriptor = {
        index,
        tabId: panelId,
        isActive: Boolean(isActive),
        isDSMobile: Boolean(isDSMobile),
        content: shouldRender && !disabled ? content : null,
        style: {
          ...(tabPanelsProps?.style || {}),
          ...(style || {}),
        } as React.CSSProperties,
        tabPanelsProps,
        'aria-hidden': !(isActive && !applyAriaDisabled),
        'data-panel-id': panelId,
        'data-testid': 'ds-tabs-tab-panel',
        hide: !isActive,
        onClick: (event: React.MouseEvent<HTMLDivElement>) => globalClickHandler({ event, target: 'panel' }),
        role: 'tabpanel',
        getOwnerProps,
        getOwnerPropsArguments,
      };

      return baseDescriptor;
    });

    // Children.map can return null if there are no children; ensure an array is returned
    return mapResult || [];
  }, [tabs, actualActiveTab, onlyRenderActiveTab, tabPanelsProps, globalClickHandler, isDSMobile]);

  // Safely compute the active slide index. Default to 0 when none found.
  const idx = panels.findIndex((panel) => panel.isActive);
  const activeSlide = idx >= 0 ? idx : 0;
  const handleOnSlide = React.useCallback(
    (index: number) => {
      handleOnChangeIndex(index, activeSlide);
    },
    [handleOnChangeIndex, activeSlide],
  );

  return (
    <StyledDSSwipeCard
      activeSlide={activeSlide}
      onSwipe={handleOnSlide}
      enableTouch
      enableAnimation={userDidChangeTabAtleastOnceRef.current && animated}
      CardRender={CardRender}
      slidesProps={panels}
      getOwnerProps={getTabPanelsOwnerProps}
      getOwnerPropsArguments={getTabPanelsOwnerPropsArguments}
    />
  );
};
